import { useEffect, useState } from 'react';

import type { CloudApiClient, CloudWorkspace } from './api-client';
import {
  loadWorkspaceCache,
  saveWorkspaceCache,
  selectAvailableWorkspace,
} from './workspace-cache';

type WorkspaceState = Readonly<{
  error: Error | null;
  items: readonly CloudWorkspace[];
  selectedWorkspaceId: string | null;
  status: 'error' | 'loading' | 'ready';
}>;

export type CloudWorkspaces = WorkspaceState &
  Readonly<{
    createWorkspace: (name: string) => Promise<void>;
    reload: () => Promise<void>;
    selectWorkspace: (workspaceId: string) => void;
  }>;

export function useCloudWorkspaces(
  api: CloudApiClient,
  subject: string,
  isOffline: boolean,
): CloudWorkspaces {
  const [state, setState] = useState<WorkspaceState>(() =>
    stateFromCache(subject),
  );

  async function reload(): Promise<void> {
    if (isOffline) {
      setState(stateFromCache(subject));
      return;
    }
    setState(current => ({ ...current, error: null, status: 'loading' }));
    try {
      const items = await api.listWorkspaces();
      setState(current => {
        const cache = saveWorkspaceCache(
          subject,
          items,
          current.selectedWorkspaceId,
        );
        return { ...cache, error: null, status: 'ready' };
      });
    } catch (error) {
      setState(current => ({
        ...current,
        error: toError(error),
        status: 'error',
      }));
    }
  }

  useEffect(() => {
    if (isOffline) {
      setState(stateFromCache(subject));
      return;
    }
    let isCurrent = true;
    setState(current => ({ ...current, error: null, status: 'loading' }));
    void api
      .listWorkspaces()
      .then(items => {
        if (!isCurrent) return;
        setState(current => {
          const cache = saveWorkspaceCache(
            subject,
            items,
            current.selectedWorkspaceId,
          );
          return { ...cache, error: null, status: 'ready' };
        });
      })
      .catch(error => {
        if (!isCurrent) return;
        setState(current => ({
          ...current,
          error: toError(error),
          status: 'error',
        }));
      });
    return () => {
      isCurrent = false;
    };
  }, [api, isOffline, subject]);

  async function createWorkspace(name: string): Promise<void> {
    const normalizedName = name.trim();
    if (isOffline) throw new Error('Workspace creation requires connectivity.');
    if (normalizedName.length === 0 || normalizedName.length > 120) {
      throw new Error(
        'Workspace name must contain between 1 and 120 characters.',
      );
    }

    const created = await api.createWorkspace({ name: normalizedName });
    setState(current => {
      const items = [
        ...current.items.filter(workspace => workspace.id !== created.id),
        created,
      ];
      const cache = saveWorkspaceCache(subject, items, created.id);
      return { ...cache, error: null, status: 'ready' };
    });
  }

  function selectWorkspace(workspaceId: string): void {
    setState(current => {
      const selectedWorkspaceId = selectAvailableWorkspace(
        current.items,
        workspaceId,
      );
      const cache = saveWorkspaceCache(
        subject,
        current.items,
        selectedWorkspaceId,
      );
      return { ...current, selectedWorkspaceId: cache.selectedWorkspaceId };
    });
  }

  return { ...state, createWorkspace, reload, selectWorkspace };
}

function stateFromCache(subject: string): WorkspaceState {
  const cache = loadWorkspaceCache(subject);
  return {
    error: null,
    items: cache?.items ?? [],
    selectedWorkspaceId: cache?.selectedWorkspaceId ?? null,
    status: 'ready',
  };
}

function toError(value: unknown): Error {
  return value instanceof Error
    ? value
    : new Error('Unexpected workspace error.');
}
